/**
 * Rules of the ABCDior order details dialog: which fields can be edited, what
 * the form refuses, how the status logs are shown and what is sent back to the
 * API.
 */
import { ROLES, type Role } from '@/shared/constants';
import { getImage, STORAGE } from '@/shared/lib/image';
import type { Order } from './api';
import { CS } from './order-types';
import { MONTH_NAMES } from './logic';
import {
  customTextLength,
  customTextPattern,
  maxCharsFor,
} from './custom-text';
import type { OrderProductLine } from './custom-text';

/** A pending order, for every role but DHL. */
export const isOrderEditable = (role: Role, order: Order): boolean =>
  order.customisationStatusId === CS.PENDING && role !== ROLES.DHL;

/** A pending order needs its Xpert transaction id. */
export const isFieldRequired = (order: Order, field: string): boolean =>
  order.customisationStatusId === CS.PENDING && field === 'externalOrderId';

/** Workshops never see the client data. */
export const showsClientData = (role: Role): boolean => role !== ROLES.WORKSHOP;
export const canEditAdditionalNotes = (
  role: Role,
  isEditMode: boolean,
): boolean =>
  isEditMode ||
  (
    [
      ROLES.ADMIN,
      ROLES.HEAD_OFFICE,
      ROLES.CALL_CENTER,
      ROLES.BOUTIQUE_MANAGER,
      ROLES.WORKSHOP,
    ] as Role[]
  ).includes(role);

export interface StatusLog {
  currentStatusId?: number | null | undefined;
  createdAt?: string | null | undefined;
  username?: string | null | undefined;
  emailSent?:
    { orderConfirmation?: unknown; orderPickup?: unknown } | null | undefined;
}

const logTime = (log: StatusLog): number =>
  log.createdAt ? new Date(log.createdAt).getTime() : 0;

/** The logs oldest first. */
export const sortStatusLogs = (
  logs: readonly Record<string, unknown>[] | null | undefined,
): StatusLog[] =>
  [...((logs ?? []) as readonly StatusLog[])].sort(
    (a, b) => logTime(a) - logTime(b),
  );

/** The day, the month or `h:mm a`. */
export const formatLogTime = (
  value: string | null | undefined,
  format: 'D' | 'MMMM' | 'h:mm a',
): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  if (format === 'D') return String(date.getDate());
  if (format === 'MMMM') return MONTH_NAMES[date.getMonth()] ?? '';
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours < 12 ? 'am' : 'pm';
  return `${hours % 12 || 12}:${minutes} ${suffix}`;
};

/** `moment().format('Z')`, e.g. `+02:00`. */
export const utcOffsetLabel = (now = new Date()): string => {
  const offset = -now.getTimezoneOffset();
  const sign = offset < 0 ? '-' : '+';
  const abs = Math.abs(offset);
  const hh = String(Math.trunc(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

/** The store mode of the product. */
export const storeModeLabel = (mode: unknown): string => {
  if (mode === 'event') return 'EVENT';
  if (mode === 'permanent') return 'BOUTIQUE';
  return '';
};

export const isTruthyFlag = (value: unknown): boolean =>
  value === true || value === 'true';
export const productPictureUrl = (order: Order): string | null => {
  if (!order.id) return null;
  return getImage(
    'abc',
    STORAGE.AbcDiorBack,
    `/product-pictures/${String(order.productId ?? '')}-main`,
  );
};

/** The product line of the order, from the app config. */
export const findProductLine = (
  lines: readonly Record<string, unknown>[] | null | undefined,
  name: unknown,
): OrderProductLine =>
  ((lines ?? []).find((line) => line.productLine === name) ??
    {}) as OrderProductLine;

/** The editable fields of the dialog. */
export interface OrderDetailsValues {
  customText: string;
  priority: 'true' | 'false';
  externalOrderId: string;
  externalClientId: string;
  firstName: string;
  lastName: string;
  productSize: string;
  onHold: boolean;
  notes: string;
}

export const detailsValues = (order: Order): OrderDetailsValues => ({
  customText: order.customText ?? '',
  priority: isTruthyFlag(order.priority) ? 'true' : 'false',
  externalOrderId: order.externalOrderId ?? '',
  externalClientId: order.externalClientId ?? '',
  firstName: order.firstName ?? '',
  lastName: order.lastName ?? '',
  productSize: order.productSize == null ? '' : String(order.productSize),
  onHold: !!order.onHold,
  notes: order.notes ?? '',
});

export interface DetailsFormCheck {
  /** Translation key of the message shown under the form, null when valid. */
  errorKey: string | null;
  /** A fatal error keeps the dialog in edit mode without saving. */
  fatal: boolean;
  customTextInvalid: boolean;
  orderIdInvalid: boolean;
}

const VALID: DetailsFormCheck = {
  errorKey: null,
  fatal: false,
  customTextInvalid: false,
  orderIdInvalid: false,
};

/**
 * An incorrect custom text is fatal,
 * a missing required order id is only reported (the save still goes through).
 */
export const checkDetailsForm = (
  values: OrderDetailsValues,
  order: Order,
  line: OrderProductLine,
): DetailsFormCheck => {
  const pattern = customTextPattern(order, line);
  const customTextInvalid =
    values.customText !== '' && !pattern.test(values.customText);
  if (customTextInvalid) {
    return {
      errorKey: 'orders.incorrectCustomText',
      fatal: true,
      customTextInvalid,
      orderIdInvalid: false,
    };
  }
  const orderIdInvalid =
    isFieldRequired(order, 'externalOrderId') && values.externalOrderId === '';
  if (orderIdInvalid) {
    return {
      errorKey: 'orders.pleaseEnterOrderId',
      fatal: false,
      customTextInvalid: false,
      orderIdInvalid,
    };
  }
  return VALID;
};

/** The custom text input is marked in red only within the keyboard length. */
export const customTextWithinLength = (
  values: OrderDetailsValues,
  order: Order,
  line: OrderProductLine,
): boolean => customTextLength(values.customText) <= maxCharsFor(order, line);

/**
 * The whole order with the edited fields, the flags as
 * booleans and the product line replaced by the configured one (see
 * migration.md).
 */
export const toSavedOrder = (
  order: Order,
  values: OrderDetailsValues,
  line: OrderProductLine,
): Order => ({
  ...order,
  productLine: line,
  product: {
    ...(order.product ?? {}),
    textProps: {
      ...((order.product?.textProps as Record<string, unknown> | undefined) ??
        {}),
      colors:
        (order.product?.textProps as { colors?: unknown } | undefined)
          ?.colors ?? [],
    },
  },
  customText: values.customText,
  priority: values.priority === 'true',
  externalOrderId: values.externalOrderId,
  externalClientId: values.externalClientId,
  firstName: values.firstName,
  lastName: values.lastName,
  productSize: values.productSize,
  onHold: values.onHold,
  notes: values.notes,
  purchaseStatus: isTruthyFlag(order.purchaseStatus),
});
